/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, BoxView } from '@components/atoms'
import { mono, boFont, boShared } from '@components/systems/backoffice/backoffice.tokens'

import { useAppSelector, useAppDispatch, logDashboardActions } from '@redux/index'
import type { LogSummaryType } from '@redux/types'

import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import CircularProgress from '@mui/material/CircularProgress'
import dayjs from 'dayjs'

/** 폼 상태는 'YYYY-MM-DD' 문자열, DatePicker 만 Date 를 쓴다 (DESIGN_SYSTEM "날짜 입력") */
const toDate = (value?: string): Date | null =>
	value === undefined || value === '' ? null : dayjs(value).toDate()
const fromDate = (value: Date | null): string =>
	value === null || !dayjs(value).isValid() ? '' : dayjs(value).format('YYYY-MM-DD')

/**
 * API 로그 대시보드 — 지표 3개(Thin 큰 수) → 국가·API TOP 10(헤어라인 행) → 에러 목록.
 * 색 카드·파란 막대는 걷어냈다. 비율 막대는 ink 한 줄.
 */
const LogDashboardPage = () => {
	const dispatch = useAppDispatch()

	const summaryDate = useAppSelector(state => state.logDashboard.summaryDate)
	const summary: LogSummaryType | null = useAppSelector(
		state => state.logDashboard.summary,
	)
	const summaryStatus = useAppSelector(state => state.logDashboard.summaryStatus)

	React.useEffect(() => {
		dispatch(logDashboardActions.TRIGGER_GET_SUMMARY(true))
	}, [summaryDate])

	const handleDateChange = (value: Date | null) => {
		dispatch(logDashboardActions.SUMMARY_DATE(fromDate(value)))
	}

	const isLoading = summaryStatus === 'loading'

	const countries = summary?.topCountries ?? []
	const apis = summary?.topApis ?? []
	const errors = summary?.errors ?? []

	return (
		<View css={style}>
			<BoxView
				title="API 로그 대시보드"
				rightHeaderAccessory={
					<DatePicker
						label="날짜"
						value={toDate(summaryDate)}
						onChange={handleDateChange}
						format="yyyy-MM-dd"
						slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
					/>
				}
			>
				<View className="dashboardContainer">
					{isLoading ? (
						<View className="loadingView">
							<CircularProgress size={24} />
						</View>
					) : (
						<>
							<View className="stats">
								<View className="boStat">
									<span className="boKicker">총 요청수</span>
									<span className="boStatValue">
										{summary?.totalRequests?.toLocaleString() ?? 0}
									</span>
								</View>
								<View className="boStat">
									<span className="boKicker">에러수</span>
									<span className="boStatValue">
										{summary?.totalErrors?.toLocaleString() ?? 0}
									</span>
								</View>
								<View className="boStat">
									<span className="boKicker">평균 응답시간</span>
									<span className="boStatValue">
										{summary?.avgResponseTime ?? 0}
										<span className="unit">ms</span>
									</span>
								</View>
							</View>

							<View className="contentRow">
								<View className="halfSection">
									<span className="boKicker">국가별 요청 TOP 10</span>
									<View className="list">
										<View className="listHead country">
											<span>#</span>
											<span>국가</span>
											<span className="right">요청수</span>
											<span>비율</span>
										</View>
										{countries.map((item, idx) => {
											const pct = summary?.totalRequests
												? ((item.count / summary.totalRequests) * 100).toFixed(1)
												: '0'
											return (
												<View className="listRow country" key={item.country}>
													<span className="no">{idx + 1}</span>
													<span>{item.country || 'Unknown'}</span>
													<span className="right num">{item.count.toLocaleString()}</span>
													<View className="barWrapper">
														<View className="barTrack">
															<View className="bar" style={{ width: `${pct}%` }} />
														</View>
														<span className="pct">{`${pct}%`}</span>
													</View>
												</View>
											)
										})}
										{countries.length === 0 && <p className="boEmpty">데이터 없음</p>}
									</View>
								</View>

								<View className="halfSection">
									<span className="boKicker">API 요청 TOP 10</span>
									<View className="list">
										<View className="listHead api">
											<span>#</span>
											<span>API</span>
											<span className="right">요청수</span>
										</View>
										{apis.map((item, idx) => (
											<View className="listRow api" key={item.api}>
												<span className="no">{idx + 1}</span>
												<span className="code">{item.api}</span>
												<span className="right num">{item.count.toLocaleString()}</span>
											</View>
										))}
										{apis.length === 0 && <p className="boEmpty">데이터 없음</p>}
									</View>
								</View>
							</View>

							<View className="errorSection">
								<span className="boKicker">에러 목록</span>
								<View className="list">
									<View className="listHead error">
										<span>시간</span>
										<span>API</span>
										<span>국가</span>
										<span>상태코드</span>
										<span>에러메시지</span>
									</View>
									{errors.map((err, idx) => (
										<View className="listRow error" key={`${err.requestId}-${idx}`}>
											<span className="nowrap num">{err.timestamp}</span>
											<span className="code">
												{err.method} {err.url}
											</span>
											<span>{err.country || '-'}</span>
											<span>
												<span className="boChip solid">{err.statusCode}</span>
											</span>
											<span className="ellipsis">{err.errorMessage}</span>
										</View>
									))}
									{errors.length === 0 && <p className="boEmpty">에러 없음</p>}
								</View>
							</View>
						</>
					)}
				</View>
			</BoxView>
		</View>
	)
}

export default LogDashboardPage

const style = css({
	...boShared,
	height: '100%',

	'.dashboardContainer': {
		width: '100%',
		display: 'flex',
		flexDirection: 'column',
		gap: 40,
	},

	'.loadingView': {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		minHeight: '20rem',
	},

	'.stats': {
		display: 'grid',
		gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
		gap: 32,
		padding: '8px 0 32px',
		borderBottom: `1px solid ${mono.line}`,

		'.unit': { fontSize: 16, fontWeight: 300, color: mono.faint, marginLeft: 4 },

		'@media (max-width: 900px)': { gridTemplateColumns: '1fr', gap: 24 },
	},

	'.contentRow': {
		display: 'grid',
		gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
		gap: 48,

		'@media (max-width: 900px)': { gridTemplateColumns: '1fr' },
	},

	'.halfSection, .errorSection': { minWidth: 0 },

	'.list': {
		marginTop: 12,
		borderTop: `1px solid ${mono.line}`,

		'.listHead, .listRow': {
			display: 'grid',
			alignItems: 'center',
			gap: 16,
			padding: '10px 0',
			borderBottom: `1px solid ${mono.line}`,
			fontSize: 13,
			fontWeight: 300,
			color: mono.ink,

			'&.country': { gridTemplateColumns: '28px minmax(0, 1fr) 80px minmax(0, 1.2fr)' },
			'&.api': { gridTemplateColumns: '28px minmax(0, 1fr) 80px' },
			'&.error': {
				gridTemplateColumns: '150px minmax(0, 1.4fr) 60px 80px minmax(0, 1.6fr)',
			},
		},

		'.listHead': {
			fontSize: 11,
			fontWeight: 500,
			letterSpacing: '.12em',
			textTransform: 'uppercase',
			color: mono.faint,
		},

		'.listRow:hover': { background: mono.surface },

		'.no': {
			fontSize: 11,
			letterSpacing: '.14em',
			color: mono.faint,
			fontVariantNumeric: 'tabular-nums',
		},
		'.right': { textAlign: 'right' },
		'.num': { fontVariantNumeric: 'tabular-nums' },
		'.nowrap': { whiteSpace: 'nowrap' },
		'.code': {
			fontFamily: boFont.mono,
			fontSize: 12,
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			whiteSpace: 'nowrap',
		},
		'.ellipsis': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
	},

	'.barWrapper': {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
		minWidth: 0,

		'.barTrack': { flex: 1, height: 1, background: mono.line, position: 'relative' },
		'.bar': {
			position: 'absolute',
			left: 0,
			top: -1,
			height: 3,
			minWidth: 2,
			background: mono.ink,
		},
		'.pct': {
			flexShrink: 0,
			width: 44,
			textAlign: 'right',
			fontSize: 11,
			color: mono.dim,
			fontVariantNumeric: 'tabular-nums',
		},
	},
})
